import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { AssetTypeEnum } from 'src/app/enums/assetType.enum';
import { CalibrationEquipment } from 'src/app/models/calibrationEquipment.model';
import { DeviceCalibrationHistory } from 'src/app/models/deviceCalibrationHistory.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { DeviceCalibrationHistoryService } from 'src/app/services/deviceCalibrationHistory.service';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { DateFormField, FileAttachmentsField, SingleSelectionFormField, ToggleFormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { AttachmentsDialogComponent } from '../attachments-dialog/attachments-dialog.component';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { AuthService } from 'src/app/services';
import { FileAttachmentService } from 'src/app/services/file-attachment.service';
import { getEnumLookup } from 'src/app/_helpers/enum-helper';
import { CalibrationFrequency } from 'src/app/enums/calibrationFrequency.enum';
import { isBefore, addWeeks, addMonths, addYears } from 'date-fns';

export interface ICalibrationDialogData extends IFormDialogData {
  sensorId: string;
  calibrationDetails?: CalibrationEquipment;
}

@Component({
  standalone: false,
  selector: 'app-calibration-dialog',
  templateUrl: './../../shared/form/form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/form/form.component.scss', './calibration-dialog.component.scss']
})
export class CalibrationDialogComponent extends FormComponent<DeviceCalibrationHistory> implements OnInit {

  constructor(
    dialogRef: MatDialogRef<CalibrationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICalibrationDialogData,
    deviceCalibrationHistoryService: DeviceCalibrationHistoryService,
    protected injector: Injector,
    protected dateFormatService: DateFormatService,
    private dialog: MatDialog,
    private authService: AuthService,
    private fileAttachmentService: FileAttachmentService
  ) {
    super(dialogRef, deviceCalibrationHistoryService, data, 'Calibration', injector);
  }

  ngOnInit(): void {
    this.setTitle(this.translateService.getDynamicTranslation("Add Calibration"), false);
    this.isFormLoading = true;
    forkJoin({
      entity: this.getEntity(),
    }).subscribe({
      next: (result) => {
        this.entity = result.entity;
        this.initModals()
        this.initFields();
        this.isFormLoading = false;
      }
    })
  }

  getNewObject(): DeviceCalibrationHistory {
    return new DeviceCalibrationHistory();
  }

  validate(): boolean {
    if (this.entity.nextCalibrationDate && this.entity.calibrationDate) {
      if (isBefore(this.entity.nextCalibrationDate as Date, this.entity.calibrationDate as Date)) {
        this.toastService.displayErrorMessage("Next Calibration Date cannot be before Calibration Date", true);
        return false;
      }
    }
    return true;
  }

  initModals() {
    if (this.data.sensorId) {
      this.entity.sensorId = this.data.sensorId;
      this.entity.calibrationFrequency = null;
    }
    else if (this.data.calibrationDetails) {
      this.entity.calibrationEquipmentId = this.data.calibrationDetails.id;
      this.entity.calibrationFrequency = this.data.calibrationDetails.calibrationFrequency;
    }
    if (this.entity.certificateUrl) {
      const filter = JSON.stringify({ url: this.entity.certificateUrl });
      this.fileAttachmentService.getAll(filter).subscribe({
        next: (res) => {
          if (res?.length) this.updateAttachment(res[0]?.id);
        },
        error: (err) => {
          this.toastService?.showError?.('Failed to load attachments');
        }
      });
    }
    if (this.isAdd) {
      this.entity.calibrationStatus = false;
    }
  }

  initFields(): void {
    this.fields = [
      new DateFormField({
        property: nameOf<DeviceCalibrationHistory>(x => x.calibrationDate),
        name: 'Calibration Date',
        onChange: () => this.setNextCalibration()
      }),
      new SingleSelectionFormField({
        property: nameOf<DeviceCalibrationHistory>(x => x.calibrationFrequency),
        name: 'Calibration Frequency',
        options: getEnumLookup(CalibrationFrequency),
        readonly: this.entity.calibrationFrequency != null,
      }),
      new DateFormField({
        property: nameOf<DeviceCalibrationHistory>(x => x.nextCalibrationDate),
        name: 'Next Calibration Date'
      }),
      new ToggleFormField({
        property: nameOf<DeviceCalibrationHistory>(x => x.calibrationStatus),
        name: 'Equipment Passed Calibration?', cssClass: 'mb-4',
      }),
      new FileAttachmentsField({
        property: nameOf<DeviceCalibrationHistory>(x => x.certificateFileName), name: 'Attached  Files', options: [],
        onClick: (id) => this.updateAttachment(id), additionalLabel: () => this.translateService.getDynamicTranslation('+ Add Certificate'), required: false
      }),
    ]
  }

  setNextCalibration() {
    if (!this.entity.calibrationDate || this.entity.calibrationFrequency == null) {
      this.entity.nextCalibrationDate = null;
      return;
    }

    let dateMoment = new Date((this.entity.calibrationDate as Date).getTime());
    switch (this.entity.calibrationFrequency) {
      case CalibrationFrequency.Weekly:
        dateMoment = addWeeks(dateMoment, 1);
        break;
      case CalibrationFrequency.Monthly:
        dateMoment = addMonths(dateMoment, 1);
        break;
      case CalibrationFrequency.Annually:
        dateMoment = addYears(dateMoment, 1);
        break;
    }

    this.entity.nextCalibrationDate = dateMoment;
  }

  updateAttachment(id: number | string) {
    if (id === 0) this.openAttachmentForm();
    else if (this.entity.certificateUrl) {
      const field = this.fields.find(f => f?.property === nameOf<DeviceCalibrationHistory>(x => x.certificateFileName));
      if (field) { field.options = [{ id: id, url: this.entity.certificateUrl, name: this.entity.certificateFileName } as any] }
    };
  }

  openAttachmentForm() {
    this.dialog.open(AttachmentsDialogComponent, {
      data: {
        allowedFileTypes: ['jpg', 'jpeg', 'png', 'pdf'],
        maxFileSizeMB: 50,
        assetType: AssetTypeEnum.DeviceCalibrationHistory,
        organizationLevelId: this.authService.getCurrentUser().organizationLevelIds?.[0] || null,
        showDescription: false,
        attachFunction: (payload: FileAttachment) => {
          this.entity.certificateFileName = payload.fileKey;
          const field = this.fields.find(f => f?.property === nameOf<DeviceCalibrationHistory>(x => x.certificateFileName));
          if (field) { field.options = [{ url: '', name: payload.fileKey } as any] };
          return of({});
        }
      },
      width: '460px',
      panelClass: 'attachments-dialog',
      autoFocus: false
    });
  }

  onBeforeSave(model: DeviceCalibrationHistory): void {
    if (model.calibrationDate) model.calibrationDate = this.dateFormatService.convertStringToTimestamp(model.calibrationDate as string);
    if (model.nextCalibrationDate) model.nextCalibrationDate = this.dateFormatService.convertStringToTimestamp(model.nextCalibrationDate as string);
  }

}
